/**
 * Sync Worker Metrics Cards Component
 *
 * Displays KPI summary cards for sync worker performance metrics
 */

import { MetricCard } from './MetricCard';
import { METRIC_DESCRIPTIONS } from './MetricDescriptions';
import { formatNumber, formatDuration, formatRate } from '@/lib/formatters';
import type { SyncWorkerSummary } from '@/types/dashboard';

interface SyncWorkerMetricsCardsProps {
  data: SyncWorkerSummary | null | undefined;
  isLoading: boolean;
}

export function SyncWorkerMetricsCards({
  data,
  isLoading,
}: SyncWorkerMetricsCardsProps) {
  // Show skeleton when loading OR when data hasn't arrived yet
  const showSkeleton = isLoading || data === null || data === undefined;

  return (
    <div
      className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
      role="region"
      aria-label="Sync worker performance metrics"
    >
      <MetricCard
        title="Items Synced"
        value={formatNumber(data?.totalItemsSynced)}
        description="New items added from sync jobs"
        isLoading={showSkeleton}
        variant="success"
        tooltip={METRIC_DESCRIPTIONS.itemsSynced}
      />
      <MetricCard
        title="Items Skipped"
        value={formatNumber(data?.totalItemsSkipped)}
        description="Already existing items"
        isLoading={showSkeleton}
        tooltip={METRIC_DESCRIPTIONS.itemsSkipped}
      />
      <MetricCard
        title="Items Failed"
        value={formatNumber(data?.totalItemsFailed)}
        description="Failed to sync"
        isLoading={showSkeleton}
        threshold={
          data?.totalItemsFailed !== undefined
            ? {
                value: data.totalItemsFailed,
                warningThreshold: 1,
                criticalThreshold: 10,
                inverse: true,
              }
            : undefined
        }
        tooltip={METRIC_DESCRIPTIONS.itemsFailed}
      />
      <MetricCard
        title="Avg Processing Rate"
        value={formatRate(data?.avgProcessingRate)}
        description="Items processed per second"
        isLoading={showSkeleton}
        tooltip={METRIC_DESCRIPTIONS.avgProcessingRate}
      />
      <MetricCard
        title="Total Duration"
        value={formatDuration(data?.totalDurationSeconds)}
        description="Cumulative sync time"
        isLoading={showSkeleton}
        tooltip={METRIC_DESCRIPTIONS.totalDuration}
      />
      <MetricCard
        title="API Calls"
        value={formatNumber(data?.totalApiCalls)}
        description="External API requests made"
        isLoading={showSkeleton}
        tooltip={METRIC_DESCRIPTIONS.apiCalls}
      />
      <MetricCard
        title="S3 Saves"
        value={formatNumber(data?.totalS3Saves)}
        description="Documents saved to S3"
        isLoading={showSkeleton}
        tooltip={METRIC_DESCRIPTIONS.s3Saves}
      />
    </div>
  );
}
